import cn from "classnames";
import { Button } from "components/UI/buttons/Button";
import { Input } from "components/UI/form/Input";
import { InputFile } from "components/UI/form/InputFile";
import { Formik } from "formik";
import * as Yup from "yup";
import { axios } from "utils/httpClient";

import styles from "./form.module.scss";
import { usePathname, useRouter } from "next/navigation";
import { baseUrl } from "const";
import { Link } from "@/components/shared/Link";

type DonatePaperFormProps = {
  setSuccess: (value: boolean | undefined) => void;
};

const ALLOWED_FILE_TYPES = [
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const MAX_FILE_SIZE = 104857600; // 100 mb

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Field is required!")
    .trim()
    .min(3, "Field should contain at least 3 characters")
    .max(55, "Field should be no longer than 55 characters"),
  email: Yup.string()
    .email("Please provide a valid email address")
    .required("Field is required!")
    .trim()
    .max(99, "Field should be no longer than 99 characters"),
  title: Yup.string()
    .required("Field is required!")
    .trim()
    .min(3, "Field should contain at least 3 characters")
    .max(200, "Field should be no longer than 200 characters"),
  file: Yup.mixed()
    .required("File is required")
    .test(
      "fileType",
      "Only .doc and .docx files allowed",
      (value) => !value || ALLOWED_FILE_TYPES.includes(value.type)
    )
    .test("fileSize", '"File size is too large', (value) => !value || value.size <= MAX_FILE_SIZE),
});

export const DonatePaperForm = ({ setSuccess }: DonatePaperFormProps) => {
  const pathname = usePathname();

  const initialValues = {
    name: "",
    email: "",
    title: "",
    url: "https://" + baseUrl + pathname,
    file: undefined,
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        const data = new FormData();

        Object.entries(values).forEach(([key, value]) => {
          // @ts-ignore
          data.append(key, value);
        });

        axios
          .post("/donates", data, {
            headers: { "Content-Type": "multipart/form-data" },
          })
          .then(() => {
            resetForm();
            setSubmitting(false);
            setSuccess(true);
          })
          .catch(() => {
            setSubmitting(false);
            setSuccess(false);
          });
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
      }) => {
        return (
          <form noValidate onSubmit={handleSubmit}>
            <div className={styles.row}>
              <Input
                name="title"
                label="Title"
                placeholder="Provide the title of your paper"
                type="text"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.title}
                disabled={isSubmitting}
                error={touched.title && errors.title?.length && errors.title}
              />
            </div>
            <div className={styles.row}>
              <Input
                name="name"
                label="Name"
                placeholder="Provide your name"
                type="text"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.title}
                disabled={isSubmitting}
                error={touched.title && errors.name?.length && errors.name}
              />
            </div>
            <div className={styles.row}>
              <Input
                name="email"
                label="Email"
                placeholder="Provide your email"
                type="email"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                disabled={isSubmitting}
                error={touched.email && errors.email?.length && errors.email}
              />
            </div>
            <InputFile
              name="file"
              onChange={setFieldValue}
              disabled={isSubmitting}
              setFieldTouched={setFieldTouched}
              error={touched.file && errors.file?.length && errors.file}
            />
            <div className={cn(styles.row, styles.rowCenter)}>
              <Button loading={isSubmitting} tag="button" type="secondary" className={styles.bigButton}>
                Upload essay
              </Button>
            </div>
            <p className={cn(styles.note, styles.noteSmall)}>
              By clicking &quot;Send&quot;, you agree to our{" "}
              <Link href="/terms-and-conditions" className="link">
                Terms and Conditions
              </Link>
              .
              <br />
              We occasionally will send you account related and promo emails.
            </p>
          </form>
        );
      }}
    </Formik>
  );
};
